import React, { useEffect, useState } from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery';

// Landing-hero use-case line. This IS the hero heading — it replaced the
// previous "Drop audio. Get a link. Share it anywhere." H1. The component is
// named …Typewriter for backwards compatibility with the Hero.tsx import site;
// the mechanic is no longer a typewriter — see below.
//
// Design pivot (2026-07-03):
//   The prior version was an independent-slots typewriter — three parameter
//   arrays (persons/jobs/risks), 3×3×3 = 27 permutations, one slot animating
//   char-by-char at a time. Two problems retired that approach:
//     1. Coherence-by-luck. Independent slots ship nonsense triples in
//        practice ("voice-memo journalist who needs to get a second opinion
//        on a mix" — journalists don't mix). No amount of slot-level rewriting
//        removed the risk entirely.
//     2. Readability during motion. Char-by-char delete/type inside a
//        `[bracketed orange bold]` slot reflows the surrounding text
//        horizontally on every keystroke, which is very hard to read while
//        it's transitioning. The bracketed-orange treatment reads best when
//        it's still.
//   The current mechanic ditches independent slots and character animation
//   entirely. Instead, we hand-pick a small set of fully coherent triples
//   (persona + job + risk that all match) and CROSSFADE between them. Whole
//   line fades out, next combo fades in. Deterministic cycle in declaration
//   order. Every combo is a coherent sentence because a human wrote it that
//   way — no more independent-slot honesty check.
//
// Visual language of the slots — LOAD-BEARING:
//   - Slot contents render in soundwave-500 orange (#ff4800) at
//     font-extrabold at the h1's inherited font-size. The scaffold ("This is
//     for", "who needs to", "without") stays at font-bold AND drops down to
//     0.9em — a sniff smaller than the orange fills, so the parameters read
//     as slightly heavier and slightly larger than the connective prose
//     around them. The 0.9em is expressed relative to the h1 (em unit), so
//     the ratio holds across the text-3xl mobile / text-5xl desktop split
//     without a second responsive class. Baseline-aligned, so the different
//     font-sizes still sit on the same alphabetic baseline.
//   - Color + weight + size differential are the "this is a variable" cues.
//     No bracket punctuation around the fills — those got removed on
//     2026-07-04 because the brackets read as engineering-typography rather
//     than as prose, and the visual treatment alone carries the "swappable"
//     signal without the visual noise.
//   - Do NOT reintroduce brackets. Do NOT wrap the whole sentence in any
//     surround. Only the three fills carry the accent treatment.
//   - The combos below still share the fixed scaffold, so the visual
//     language reads the same on every combo — only the parameters change on
//     each crossfade.
//
// Choreography:
//   HOLD_MS (6000) — combo sits fully visible, opacity 1. Snappier than the
//                    previous 8500ms hold Glenn tuned in `5b73ec8` — 2026-07-07
//                    ask was "increase the speed a bit on the animation,
//                    maybe 30% faster." 6000ms is ~29% faster and reads as
//                    "there's another one coming soon" rather than "settle in
//                    and read this one carefully." Tradeoff acknowledged: the
//                    prior 8500ms hold was tuned to fully accommodate the
//                    worst-case ~131-char sentence (~28 words ≈ 7s at typical
//                    reading speed) + 1s dwell. At 6000ms the worst-case
//                    reader is still mid-sentence when the fade starts, but
//                    the hero-reading task is now closer to skimming than
//                    studying — Glenn's call, deliberate.
//   FADE_MS (700)  — whole heading crossfades to opacity 0. Unchanged from
//                    `5b73ec8`. The slower fade reads as calm rather than a
//                    flip; 700ms is perceptibly slower than the prior 350ms
//                    without crossing into sluggish territory. Not part of
//                    the 30% speedup — Glenn's ask was scoped to "the
//                    animation" (the countdown fill, which is coupled to
//                    HOLD_MS), not the crossfade envelope.
//                    On the fade-out completing, the combo index advances and
//                    the new combo mounts at opacity 0.
//   FADE_MS (700)  — new combo crossfades from opacity 0 → 1.
//   → back to HOLD_MS.
//   No character-level animation, no horizontal reflow during transitions,
//   no caret. Cycle is deterministic ([0] → [1] → … → [N-1] → [0] → …).
//
// Countdown bar — a hairline pill (~80px wide, 3px tall, soundwave-500 at
// low opacity on a very faint white track) sits centered below the H1. Fills
// left → right from 0% to 100% over HOLD_MS via the `hero-countdown`
// keyframe (App.css). Re-mounts on each combo advance via `key={comboIdx}`,
// which restarts the animation from 0%. Sits OUTSIDE the H1's opacity
// crossfade on purpose: the H1 fades away and back for the sentence swap,
// but the bar stays visible across the transition as continuity — the reader
// senses "the next sentence is coming" without having to notice the fade
// itself. Container has fixed height, so adding it does not shift the H1's
// layout envelope or any of the cards below it.
//
// Reserved height (text-3xl mobile / text-5xl desktop, leading-tight ≈ 1.25):
//   Scaffold (no brackets) = "This is for " (12) + " who needs to " (14)
//   + " without " (9) + "." (1) = 36 chars. The bracket removal on 2026-07-04
//   trimmed 6 chars from every combo (three "[" + three "]"), so the current
//   worst case is ~131 chars — strictly smaller than the prior 137-char
//   envelope, so the reserved height is unchanged.
//   Two combos added on 2026-07-05 (Suno user, content creator); neither
//   exceeds the ~131-char worst case (Suno ≈ 122, content creator ≈ 97), so
//   the envelope still holds and the reservation stays.
//   Mobile text-3xl (~30px) at leading-tight (~37.5px line-height) in
//     max-w-3xl at ~340px content width, ~15px avg lowercase glyph → ~22
//     chars/line → 131 / 22 ≈ 6 lines → ~225px needed. Reserved 17rem (272px).
//   Desktop text-5xl (~48px) at leading-tight (~60px line-height) in
//     max-w-3xl at ~768px content width, ~26px avg lowercase glyph → ~29
//     chars/line → 131 / 29 ≈ 5 lines → ~300px needed. Reserved 19rem (304px).
//   If you edit any combo or add a new one, hand-check the longest one
//   against these envelopes at 320px and 1440px viewports.
//
// prefers-reduced-motion: render the first combo statically at opacity 1, no
// crossfade cycle and no countdown bar (no cycle → nothing to count down).
// Same reserved footprint as the animated path.

// Hand-picked, coherent triples. Each entry is a persona + job + risk that
// all cohere as a real use case a stranger would recognize. Cycle order
// within the array is deterministic ([i] → [i+1] → … [N-1] → [0] → …), but
// the STARTING index is randomized per mount (see comboIdx useState below) —
// a returning visitor should not always land on the same first combo. This
// is not shuffling: the cycle itself is still stable, only the entry point
// varies.
//
// Content guardrails Glenn has locked:
//   - No church sound tech / worship tech / any variant. Not a market
//     pastewaves targets. Rejected more than once.
//   - Voice-memo journalists share interview clips with editors — they do
//     not mix. Anchor to what the persona actually does.
//   - Personas are specific enough that a stranger recognizes their own
//     workflow, not generic startup-page stand-ins.
//   - Every risk is real to that persona (either a friction they know or
//     something they actively avoid); no jargon.
//   - Glenn's voice: humble, use-case-led, no exclamation, no pressure.
//
// Declaration order is the cycle order. Append-at-end is the default so the
// front of the cycle stays stable, but pacing can override — when a new
// triple would cluster same-length jobs together (all long, all short), insert
// it where its cadence contrasts with its neighbors. The two 2026-07-05 adds
// (positions 5 and 8) are placed for pacing, not appended.
const COMBOS: Array<{ person: string; job: string; risk: string }> = [
  {
    person: 'a piano teacher',
    job: "send this week's take to a student",
    risk: 'emailing a 200 MB file',
  },
  {
    person: 'a songwriter',
    job: 'share a rough demo with a collaborator',
    risk: 'it living on SoundCloud forever',
  },
  {
    person: 'a band',
    job: 'send a practice take to the drummer',
    risk: 'anyone signing up for anything',
  },
  {
    // Home producer sharing a WIP mix for feedback. The prior risk phrase
    // ("the file showing up in a Google search") misfired — SEO indexing
    // isn't what home producers actually fear. The real friction is being
    // pushed into a Dropbox handoff, which matches the "name the competing
    // friction tool" pattern already established across the rotation
    // (email, WeTransfer, Apple Music, video editors). "Dragging them into
    // Dropbox" uses sender-perspective vocabulary — the sharer knows the
    // recipient didn't sign up for this.
    person: 'a home producer',
    job: 'get a second opinion on a mix',
    risk: 'dragging them into Dropbox',
  },
  {
    // Glenn's shorthand: "listen to tracks while working out." The scaffold's
    // "without X" slot doesn't take a "while" clause, so the workout context
    // moves into the job ("on a run" — specific, honest) and the risk names
    // the actual friction a Suno user hits getting generated audio into a
    // gym-ready listening flow (Apple Music won't play arbitrary files without
    // a library import).
    person: 'a Suno user',
    job: 'listen to their generated tracks on a run',
    risk: 'loading them into Apple Music first',
  },
  {
    person: 'a hobbyist podcaster',
    job: 'send an unlisted rough cut to a co-host',
    risk: 'publishing it publicly first',
  },
  {
    person: 'a voice-memo journalist',
    job: 'hand off an interview clip to an editor',
    risk: 'explaining what WeTransfer is',
  },
  {
    // Audio-only piece on YouTube — the actual friction is being forced into
    // a video-tool workflow (DaVinci, Premiere, CapCut) just to fake a video
    // shell around the audio. "opening a video editor" names the tool the
    // user didn't want to open, without snark.
    person: 'a content creator',
    job: 'share audio on YouTube',
    risk: 'opening a video editor',
  },
  {
    person: 'a language tutor',
    job: 'share pronunciation drills with a student',
    risk: 'another platform email in the inbox',
  },
];

const HOLD_MS = 6000;
const FADE_MS = 700;

// Per-word font-size variance — a subtle "hand-set" delight (Glenn 2026-07-02:
// "I know this is details, but I love details. Slightly randomize the words by
// adjusting font size, maybe 10% variance"). Each word gets an em multiplier
// in [0.9, 1.1] on top of its register's base (0.9 for scaffold connectives,
// 1.0 for orange fills), deterministically seeded by (comboIdx, runIdx,
// wordIdx-within-run) so:
//   - Sizes are stable within a combo's lifetime — no jitter on re-render
//     (visible-state toggles during the crossfade won't reshuffle sizes).
//   - Sizes refresh when the combo advances — the natural crossfade absorbs
//     the transition.
//   - React strict-mode double-renders in dev produce identical output because
//     the seed doesn't consult a mutable source.
// Math.sin fractional-part hash — deterministic, cheap, adequate entropy for
// a subtle typography variance where we don't need cryptographic randomness.
const wordSize = (base: number, comboIdx: number, runIdx: number, wordIdx: number): number => {
  const seed = comboIdx * 100000 + runIdx * 1000 + wordIdx;
  const raw = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
  const frac = raw - Math.floor(raw); // [0, 1)
  return base * (0.9 + frac * 0.2);
};

// Split a text run into per-word spans, each with its own em font-size.
// Whitespace tokens between words are preserved as text nodes so the browser
// breaks lines naturally at word boundaries. slotClass is applied only to
// fill runs (the orange soundwave-500 extrabold treatment); scaffold runs
// pass undefined so they inherit the h1's default weight/color.
const renderRun = (
  text: string,
  base: number,
  comboIdx: number,
  runIdx: number,
  slotClass?: string,
): React.ReactNode[] => {
  const tokens = text.split(/(\s+)/).filter((s) => s.length > 0);
  let wordIdx = 0;
  return tokens.map((token, i) => {
    if (/^\s+$/.test(token)) return <React.Fragment key={i}>{token}</React.Fragment>;
    const size = wordSize(base, comboIdx, runIdx, wordIdx);
    wordIdx += 1;
    return (
      <span
        key={i}
        aria-hidden="true"
        className={slotClass}
        style={{ fontSize: `${size.toFixed(3)}em` }}
      >
        {token}
      </span>
    );
  });
};

// Renders the full "This is for X who needs to Y without Z." sentence with
// per-word variance applied to every word (both scaffold connectives and
// fills). Screen-reader sr-only span carrying the plain sentence is emitted
// separately by the caller (it stays outside the variance treatment because
// screen readers don't care about size and would be confused by per-word
// spans).
const renderSentence = (
  combo: { person: string; job: string; risk: string },
  comboIdx: number,
  slotClass: string,
): React.ReactNode => (
  <>
    {renderRun('This is for ', 0.9, comboIdx, 0)}
    {renderRun(combo.person, 1.0, comboIdx, 1, slotClass)}
    {renderRun(' who needs to ', 0.9, comboIdx, 2)}
    {renderRun(combo.job, 1.0, comboIdx, 3, slotClass)}
    {renderRun(' without ', 0.9, comboIdx, 4)}
    {renderRun(combo.risk, 1.0, comboIdx, 5, slotClass)}
    {renderRun('.', 0.9, comboIdx, 6)}
  </>
);

const HeroUseCaseTypewriter: React.FC = () => {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  // Which combo is currently mounted. Advances after each fade-out.
  // Initial index is randomized once per mount so a returning visitor doesn't
  // always land on the same combo (Glenn's ask 2026-07-02: "It always starts
  // on the piano teacher"). The cycle order after that is still deterministic;
  // this just varies the entry point. Lazy initializer form so Math.random
  // fires once, not on every re-render.
  const [comboIdx, setComboIdx] = useState<number>(
    () => Math.floor(Math.random() * COMBOS.length),
  );
  // Opacity of the currently mounted combo. 1 during hold; 0 during fade-out
  // and immediately after a combo advance (so the next combo mounts invisible
  // before the fade-in kicks it back to 1).
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (prefersReducedMotion) return;

    // Two-phase cycle driven by `visible`:
    //   visible === true  → hold HOLD_MS, then setVisible(false) to fade out.
    //   visible === false → wait FADE_MS for the CSS opacity transition to
    //                       complete, advance comboIdx, then setVisible(true)
    //                       to fade the new combo in. Because we're advancing
    //                       comboIdx and setting visible in the same tick,
    //                       React batches them and the new combo mounts at
    //                       opacity 0 with the transition still on the
    //                       element — the fade-in happens on the next paint.
    if (visible) {
      const t = window.setTimeout(() => setVisible(false), HOLD_MS);
      return () => window.clearTimeout(t);
    }
    const t = window.setTimeout(() => {
      setComboIdx((i) => (i + 1) % COMBOS.length);
      setVisible(true);
    }, FADE_MS);
    return () => window.clearTimeout(t);
  }, [visible, prefersReducedMotion]);

  const combo = COMBOS[comboIdx];

  // Slot fills stay at the h1's inherited font-size (1em relative to the h1
  // block): text-soundwave-500 orange (#ff4800) + font-extrabold. Color and
  // weight do the "swappable variable" work that brackets used to signal in a
  // prior version.
  const slotClass = 'text-soundwave-500 font-extrabold';

  // Screen readers get the current combo as a single sentence — no aria-live
  // announcement per transition (the crossfade is decorative; the sentence
  // shape is the message). No brackets in the spoken sentence either — a
  // screen reader would read "[" as "open square bracket" which is nonsense
  // for a heading.
  const spokenSentence = `This is for ${combo.person} who needs to ${combo.job} without ${combo.risk}.`;

  // Reduced motion: static first combo, no crossfade. Same orange + weight
  // treatment on the fills so the motion and no-motion paths convey the same
  // visual language.
  if (prefersReducedMotion) {
    // Use the randomized starting combo (comboIdx) rather than COMBOS[0] so
    // reduced-motion users also get variety across visits — otherwise a
    // reduced-motion user would always see the exact same static combo, which
    // was the original "always piano teacher" complaint. Static hold (no
    // cycle) is the a11y contract; that stays. Per-word variance also applies
    // here — it's a purely visual "hand-set" delight, not motion, so
    // prefers-reduced-motion doesn't gate it.
    const first = COMBOS[comboIdx];
    // min-h bumped from 17/19rem → 19/21rem to absorb the +10% line-box worst
    // case (a line where every word happens to land at 1.1em multiplier is
    // 10% taller; the reservation now has slack for that path).
    return (
      <h1
        className="text-3xl md:text-5xl font-bold tracking-tight leading-tight mb-4 min-h-[19rem] md:min-h-[21rem]"
        style={{ color: 'var(--text-primary)' }}
      >
        <span className="sr-only">{spokenSentence}</span>
        {renderSentence(first, comboIdx, slotClass)}
      </h1>
    );
  }

  return (
    <>
      <h1
        className="text-3xl md:text-5xl font-bold tracking-tight leading-tight mb-4 min-h-[19rem] md:min-h-[21rem]"
        style={{
          color: 'var(--text-primary)',
          opacity: visible ? 1 : 0,
          transition: `opacity ${FADE_MS}ms ease-in-out`,
        }}
      >
        <span className="sr-only">{spokenSentence}</span>
        {renderSentence(combo, comboIdx, slotClass)}
      </h1>
      {/* Countdown bar — see the "Countdown bar" block in the top doc-comment.
          The wrapper is centered, fixed 3px tall, ~80px wide with a very
          faint track. The inner span carries the moving fill; its
          key={comboIdx} re-mounts it on each combo advance so the animation
          restarts from 0%. Sits outside the H1's opacity fade so it stays
          present as continuity across the transition. */}
      <div
        aria-hidden="true"
        className="mx-auto mb-6 h-[3px] w-20 overflow-hidden rounded-full bg-white/[0.06]"
      >
        <span
          key={comboIdx}
          className="block h-full rounded-full bg-soundwave-500/50"
          style={{
            width: '0%',
            animation: `hero-countdown ${HOLD_MS}ms linear forwards`,
          }}
        />
      </div>
    </>
  );
};

export default HeroUseCaseTypewriter;
